import { useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import {
  Users,
  UserCheck,
  ShieldCheck,
  AlertTriangle,
  Plus,
  X,
  Loader2,
} from 'lucide-react';

interface Driver {
  id: string;
  name: string;
  email: string;
  phone: string;
  license_number: string;
  license_class: string;
  license_state: string;
  license_expiry: string;
  hire_date: string;
  emergency_contact: string;
  emergency_phone: string;
  assigned_vehicle_id: string | null;
  assigned_vehicle_name: string | null;
  status: 'active' | 'inactive' | 'suspended';
}

interface DriversResponse {
  drivers: Driver[];
  metrics: {
    total_drivers: number;
    active: number;
    cdl_holders: number;
    expiring_licenses: number;
  };
}

interface VehicleOption { id: string; name: string }

const statusColors: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  inactive: 'bg-muted-foreground/20 text-muted-foreground border-border',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const LICENSE_CLASSES = ['Class A', 'Class B', 'Class C', 'CDL-A', 'CDL-B', 'CDL-C', 'Motorcycle', 'Other'] as const;

export default function DriversPage() {
  const { data, isLoading, refetch } = useApi<DriversResponse>('/api/drivers');
  const { data: vehiclesData } = useApi<{ vehicles: VehicleOption[] }>('/api/vehicles?pageSize=100');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const metrics = data?.metrics ?? { total_drivers: 0, active: 0, cdl_holders: 0, expiring_licenses: 0 };
  const drivers = data?.drivers ?? [];
  const vehicles = vehiclesData?.vehicles ?? [];

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const fd = new FormData(e.currentTarget);
    const selectedVehicle = vehicles.find(v => v.id === fd.get('assigned_vehicle_id'));
    try {
      await apiFetch('/api/drivers', {
        method: 'POST',
        body: JSON.stringify({
          name: fd.get('name'),
          email: fd.get('email') || undefined,
          phone: fd.get('phone') || undefined,
          license_number: fd.get('license_number') || undefined,
          license_class: fd.get('license_class') || undefined,
          license_state: fd.get('license_state') || undefined,
          license_expiry: fd.get('license_expiry') || undefined,
          hire_date: fd.get('hire_date') || undefined,
          assigned_vehicle_id: fd.get('assigned_vehicle_id') || undefined,
          assigned_vehicle_name: selectedVehicle?.name ?? undefined,
          emergency_contact: fd.get('emergency_contact') || undefined,
          emergency_phone: fd.get('emergency_phone') || undefined,
          notes: fd.get('notes') || undefined,
          status: 'active',
        }),
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add driver');
    } finally {
      setSaving(false);
    }
  }, [vehicles, refetch]);

  const metricCards = [
    { label: 'Total Drivers', value: metrics.total_drivers, icon: Users, color: 'text-emerald-400' },
    { label: 'Active', value: metrics.active, icon: UserCheck, color: 'text-emerald-400' },
    { label: 'CDL Holders', value: metrics.cdl_holders, icon: ShieldCheck, color: 'text-blue-400' },
    { label: 'Expiring Licenses', value: metrics.expiring_licenses, icon: AlertTriangle, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <Users className="h-6 w-6" style={{ color: '#10B981' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Drivers</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Manage drivers, licenses, and assignments</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-foreground transition-colors"
          style={{ background: '#10B981' }}
        >
          <Plus className="h-4 w-4" />
          Add Driver
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{m.label}</span>
              <m.icon className={`h-5 w-5 ${m.color}`} />
            </div>
            <p className="mt-2 text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>{m.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: 'var(--glass-border)', color: 'var(--muted-foreground)' }}>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">License #</th>
                <th className="px-4 py-3 font-medium">License Class</th>
                <th className="px-4 py-3 font-medium">License Expiry</th>
                <th className="px-4 py-3 font-medium">Assigned Vehicle</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center" style={{ color: 'var(--muted-foreground)' }}>
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : drivers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center" style={{ color: 'var(--muted-foreground)' }}>
                    <Users className="w-6 h-6 mx-auto mb-2 opacity-40" />
                    <p>No drivers found. Add your first driver to get started.</p>
                  </td>
                </tr>
              ) : (
                drivers.map((driver) => {
                  const isExpiring = driver.license_expiry && new Date(driver.license_expiry) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                  return (
                    <tr key={driver.id} className="border-b transition-colors hover:bg-white/5" style={{ borderColor: 'var(--glass-border)' }}>
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>{driver.name}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{driver.email ?? '--'}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{driver.phone ?? '--'}</td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>{driver.license_number ?? '--'}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{driver.license_class ?? '--'}</td>
                      <td className="px-4 py-3" style={{ color: isExpiring ? '#EF4444' : 'var(--muted-foreground)' }}>
                        {driver.license_expiry ? new Date(driver.license_expiry).toLocaleDateString() : '--'}
                        {isExpiring && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{driver.assigned_vehicle_name ?? '--'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${statusColors[driver.status] ?? ''}`}>
                          {driver.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Driver Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Add Driver</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-white/10">
                <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            {formError && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Full Name" required>
                <input type="text" name="name" required className="form-input" placeholder="John Doe" />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Email">
                  <input type="email" name="email" className="form-input" placeholder="john@example.com" />
                </FormField>
                <FormField label="Phone">
                  <input type="tel" name="phone" className="form-input" placeholder="(555) 555-1234" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="License Number">
                  <input type="text" name="license_number" className="form-input" placeholder="DL12345678" />
                </FormField>
                <FormField label="License Class">
                  <select name="license_class" className="form-select">
                    <option value="">Select...</option>
                    {LICENSE_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="License State">
                  <input type="text" name="license_state" className="form-input" placeholder="NY" maxLength={2} />
                </FormField>
                <FormField label="License Expiry">
                  <input type="date" name="license_expiry" className="form-input" />
                </FormField>
              </div>
              <FormField label="Hire Date">
                <input type="date" name="hire_date" className="form-input" />
              </FormField>
              <FormField label="Assigned Vehicle">
                <select name="assigned_vehicle_id" className="form-select">
                  <option value="">None</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Emergency Contact">
                  <input type="text" name="emergency_contact" className="form-input" placeholder="Jane Doe" />
                </FormField>
                <FormField label="Emergency Phone">
                  <input type="tel" name="emergency_phone" className="form-input" placeholder="(555) 555-5678" />
                </FormField>
              </div>
              <FormField label="Notes">
                <textarea name="notes" rows={2} className="form-input" placeholder="Additional notes..." />
              </FormField>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm rounded-lg" style={{ color: 'var(--muted-foreground)' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg flex items-center gap-2"
                  style={{ background: '#10B981', opacity: saving ? 0.7 : 1 }}>
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                  Add Driver
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
